import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CHANNEL_LABELS,
  templateKeyLabel,
  type MessageChannel,
  type OutboxMessage,
  type OutboxStatus,
} from "@shared/modules/x-messagerie";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input, Select } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { formatDateTime } from "../../../core/lib/format";
import { useToastStore } from "../../../core/stores/toast";
import { ChannelBadge, StatusBadge } from "../components/badges";
import { NewMessageModal } from "../components/new-message-modal";
import { RelatedLink } from "../components/related-link";
import { cancelMessage, listOutbox, retryMessage } from "../lib/api";

const TABS: Array<{ key: string; label: string; status?: OutboxStatus }> = [
  { key: "tous", label: "Tous" },
  { key: "en_attente", label: "En attente", status: "en_attente" },
  { key: "envoyes", label: "Envoyés", status: "envoye" },
  { key: "simules", label: "Simulés", status: "simule" },
  { key: "echecs", label: "Échecs", status: "echec" },
  { key: "annules", label: "Annulés", status: "annule" },
];

const PREVIEW_LENGTH = 90;

function preview(m: OutboxMessage): string {
  const text = m.body.replace(/\s+/g, " ").trim();
  return text.length > PREVIEW_LENGTH ? `${text.slice(0, PREVIEW_LENGTH)}…` : text;
}

/** Boîte d'envoi : onglets par statut, filtre de canal, recherche, actions. */
export function MessagesPage() {
  const push = useToastStore((s) => s.push);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === searchParams.get("onglet")) ?? TABS[0]!;
  const [channel, setChannel] = useState<MessageChannel | "">("");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState<OutboxMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [composing, setComposing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listOutbox({
      status: tab.status,
      channel: channel || undefined,
      q: debounced || undefined,
      limit: 300,
    })
      .then((list) => {
        if (cancelled) return;
        setItems(list);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, channel, debounced, reloadKey]);

  const selectTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("onglet", key);
    setSearchParams(next, { replace: true });
  };

  const act = async (m: OutboxMessage, action: "cancel" | "retry") => {
    setBusyId(m.id);
    try {
      const updated = action === "cancel" ? await cancelMessage(m.id) : await retryMessage(m.id);
      setItems((list) => list.map((x) => (x.id === updated.id ? updated : x)));
      push(
        updated.status === "echec" ? "danger" : "success",
        action === "cancel"
          ? "Message annulé"
          : updated.status === "en_attente"
            ? "Message remis en attente"
            : "Message renvoyé",
        updated.status === "echec" ? (updated.error ?? undefined) : undefined,
      );
    } catch {
      push(
        "danger",
        action === "cancel" ? "Annulation impossible" : "Réessai impossible",
        "Le message a peut-être déjà changé d'état.",
      );
      setReloadKey((k) => k + 1);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page messages">
      <PageHeader
        title="Messages"
        subtitle="Textos et courriels envoyés aux clients : confirmations, rappels, véhicule prêt, avis."
        actions={
          <Button
            variant="primary"
            icon={<Icon name="plus" size={16} />}
            onClick={() => setComposing(true)}
          >
            Nouveau message
          </Button>
        }
      />

      <div className="messagerie-tabs" role="tablist" aria-label="Statut">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab.key === t.key}
            className={`messagerie-tabs__btn${tab.key === t.key ? " is-active" : ""}`}
            onClick={() => selectTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="messagerie-toolbar">
        <div className="messagerie-toolbar__search">
          <Input
            type="search"
            aria-label="Rechercher"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Destinataire, objet, contenu…"
          />
        </div>
        <Select
          className="messagerie-toolbar__select"
          aria-label="Canal"
          value={channel}
          onChange={(e) => setChannel(e.target.value as MessageChannel | "")}
        >
          <option value="">Tous les canaux</option>
          {Object.entries(CHANNEL_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </Select>
        <div className="messagerie-toolbar__spacer" />
        <span className="messagerie-toolbar__count">
          {loading ? "…" : `${items.length} message${items.length > 1 ? "s" : ""}`}
        </span>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Canal</th>
            <th>Destinataire</th>
            <th>Objet / aperçu</th>
            <th>Lié à</th>
            <th>Statut</th>
            <th className="table__actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id}>
              <td className="messages__date">
                {m.status === "en_attente" && m.scheduledAt ? (
                  <>
                    <span className="messages__muted">Prévu</span>{" "}
                    {formatDateTime(m.scheduledAt, { dateStyle: "medium", timeStyle: "short" })}
                  </>
                ) : (
                  formatDateTime(m.sentAt ?? m.createdAt, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })
                )}
              </td>
              <td>
                <ChannelBadge channel={m.channel} />
              </td>
              <td className="messages__to">{m.toAddress}</td>
              <td className="messages__preview">
                {m.subject ? <span className="messages__subject">{m.subject}</span> : null}
                <span className="messages__body" title={m.body}>
                  {preview(m)}
                </span>
                {m.templateKey ? (
                  <span className="messages__template">{templateKeyLabel(m.templateKey)}</span>
                ) : null}
              </td>
              <td>
                <RelatedLink type={m.relatedType} id={m.relatedId} />
              </td>
              <td>
                <StatusBadge status={m.status} />
                {m.error ? (
                  <span className="messages__error" title={m.error}>
                    {m.error}
                  </span>
                ) : null}
              </td>
              <td className="table__actions">
                {m.status === "en_attente" ? (
                  <Button
                    variant="ghost-danger"
                    size="sm"
                    disabled={busyId === m.id}
                    onClick={() => act(m, "cancel")}
                  >
                    Annuler
                  </Button>
                ) : null}
                {m.status === "echec" || m.status === "annule" ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busyId === m.id}
                    onClick={() => act(m, "retry")}
                  >
                    Réessayer
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
          {!loading && items.length === 0 ? (
            <tr>
              <td colSpan={7} className="table__empty">
                {failed
                  ? "Boîte d'envoi indisponible — réessayez dans un instant."
                  : debounced
                    ? "Aucun message ne correspond à la recherche."
                    : "Aucun message dans cet onglet."}
              </td>
            </tr>
          ) : null}
          {loading && items.length === 0 ? (
            <tr>
              <td colSpan={7} className="table__empty">
                Chargement…
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <NewMessageModal
        open={composing}
        onClose={() => setComposing(false)}
        onSent={() => {
          setComposing(false);
          setReloadKey((k) => k + 1);
        }}
      />
    </div>
  );
}
